import {
  Component,
  ElementRef,
  Injector,
  OnInit,
  ViewChild,
  forwardRef,
  computed,
  inject,
  signal,
  input,
  output,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';

export interface SoImageFile {
  file: File;
  preview: string;
  name: string;
  size: number;
}

export interface SoImgValidationError {
  file: File;
  errorType: 'size' | 'type' | 'dimensions';
  message: string;
}

@Component({
  selector: 'so-img-input-ui',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './so-img-input.component.html',
  styleUrl: './so-img-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoImgInputUI),
      multi: true
    }
  ]
})
export class SoImgInputUI implements ControlValueAccessor, OnInit {
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  // Modern signal-based inputs
  disabled = input<boolean>(false);
  multiple = input<boolean>(false);
  maxSize = input<number>(5);
  maxImages = input<number>(10);
  acceptedFormats = input<string[]>(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']);
  idInput = input<string>('img-input');
  showPreview = input<boolean>(true);
  previewSize = input<'small' | 'medium' | 'large'>('medium');
  aspectRatio = input<string | null>(null);
  placeholder = input<string>('Haz clic o arrastra una imagen');
  label = input<string>('');
  required = input<boolean>(false);
  
  // Layout options
  layout = input<'grid' | 'horizontal' | 'single'>('single');
  gridColumns = input<number>(3);
  
  // Dimension constraints
  minWidth = input<number | null>(null);
  minHeight = input<number | null>(null);
  maxWidth = input<number | null>(null);
  maxHeight = input<number | null>(null);

  // Outputs
  imageChanged = output<SoImageFile | SoImageFile[] | null>();
  imageRemoved = output<{ image: SoImageFile; index: number }>();
  validationErrors = output<SoImgValidationError[]>();

  // Internal state
  protected images = signal<SoImageFile[]>([]);
  protected isDragging = signal<boolean>(false);

  // ControlValueAccessor callbacks
  private onChange: (value: File | File[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  private injector = inject(Injector);
  private controlRequired = signal(false);

  // Computed values
  protected effectiveDisabled = computed(() => this.disabled());
  protected effectiveRequired = computed(() => this.required() || this.controlRequired());
  
  protected hasImages = computed(() => this.images().length > 0);
  
  protected canAddMore = computed(() => {
    if (!this.multiple()) return !this.hasImages();
    return this.images().length < this.maxImages();
  });

  protected acceptString = computed(() => this.acceptedFormats().join(','));

  protected containerClasses = computed(() => ({
    'so-img-input': true,
    'so-img-input--disabled': this.effectiveDisabled(),
    'so-img-input--dragging': this.isDragging() && !this.effectiveDisabled(),
    'so-img-input--has-images': this.hasImages(),
    [`so-img-input--${this.layout()}`]: true,
    [`so-img-input--preview-${this.previewSize()}`]: true
  }));

  protected gridStyle = computed(() => {
    if (this.layout() !== 'grid') return {};
    return {
      'grid-template-columns': `repeat(${this.gridColumns()}, 1fr)`
    };
  });

  protected previewStyle = computed(() => {
    const ratio = this.aspectRatio();
    if (!ratio) return {};
    return { 'aspect-ratio': ratio };
  });

  ngOnInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    queueMicrotask(() => {
      const control = ngControl?.control;
      if (control?.hasValidator) {
        this.controlRequired.set(control.hasValidator(Validators.required));
      }
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: File | File[] | null): void {
    if (!value) {
      this.images.set([]);
      return;
    }
    
    if (Array.isArray(value)) {
      this.processFiles(value, true);
    } else {
      this.processFiles([value], true);
    }
  }

  registerOnChange(fn: (value: File | File[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled via input signal
  }

  // Drag and Drop handlers
  onDragOver(event: DragEvent): void {
    if (this.effectiveDisabled() || !this.canAddMore()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    if (this.effectiveDisabled()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (this.effectiveDisabled() || !this.canAddMore()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    if (event.dataTransfer?.files) {
      this.processFiles(Array.from(event.dataTransfer.files));
    }
  }

  onFileSelected(event: Event): void {
    if (this.effectiveDisabled()) return;
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.processFiles(Array.from(input.files));
      input.value = '';
    }
  }

  onZoneClick(): void {
    if (this.effectiveDisabled() || !this.canAddMore()) return;
    this.fileInput?.nativeElement.click();
  }

  removeImage(index: number): void {
    const currentImages = this.images();
    const removedImage = currentImages[index];
    
    URL.revokeObjectURL(removedImage.preview);
    
    const newImages = currentImages.filter((_, i) => i !== index);
    this.images.set(newImages);
    
    this.emitChange(newImages);
    this.imageRemoved.emit({ image: removedImage, index });
  }

  clearAll(): void {
    const currentImages = this.images();
    currentImages.forEach(img => URL.revokeObjectURL(img.preview));
    
    this.images.set([]);
    this.onChange(null);
    this.imageChanged.emit(null);
  }

  // File processing
  private async processFiles(files: File[], isInitial = false): Promise<void> {
    const errors: SoImgValidationError[] = [];
    const validImages: SoImageFile[] = [];
    const currentImages = isInitial ? [] : this.images();
    const remainingSlots = this.multiple() ? this.maxImages() - currentImages.length : 1;

    const filesToProcess = files.slice(0, remainingSlots);

    for (const file of filesToProcess) {
      // Validate file type
      if (!this.isValidImageType(file)) {
        errors.push({
          file,
          errorType: 'type',
          message: `"${file.name}" no es un formato de imagen válido.`
        });
        continue;
      }

      // Validate file size
      if (file.size > this.maxSize() * 1024 * 1024) {
        errors.push({
          file,
          errorType: 'size',
          message: `"${file.name}" excede el tamaño máximo de ${this.maxSize()} MB.`
        });
        continue;
      }

      // Validate dimensions if constraints exist
      const dimensionError = await this.validateDimensions(file);
      if (dimensionError) {
        errors.push(dimensionError);
        continue;
      }

      // Create preview
      const preview = URL.createObjectURL(file);
      validImages.push({
        file,
        preview,
        name: file.name,
        size: file.size
      });
    }

    if (errors.length > 0) {
      this.validationErrors.emit(errors);
    }

    if (validImages.length > 0) {
      const updatedImages = this.multiple() 
        ? [...currentImages, ...validImages]
        : validImages.slice(0, 1);
      
      this.images.set(updatedImages);
      this.onTouched();
      this.emitChange(updatedImages);
    }
  }

  private emitChange(images: SoImageFile[]): void {
    if (this.multiple()) {
      const files = images.map(img => img.file);
      this.onChange(files.length > 0 ? files : null);
      this.imageChanged.emit(images.length > 0 ? images : null);
    } else {
      const file = images[0]?.file || null;
      this.onChange(file);
      this.imageChanged.emit(images[0] || null);
    }
  }

  private isValidImageType(file: File): boolean {
    return this.acceptedFormats().some(format => {
      if (format.endsWith('/*')) {
        return file.type.startsWith(format.replace('/*', '/'));
      }
      return file.type === format;
    });
  }

  private validateDimensions(file: File): Promise<SoImgValidationError | null> {
    return new Promise((resolve) => {
      const minW = this.minWidth();
      const minH = this.minHeight();
      const maxW = this.maxWidth();
      const maxH = this.maxHeight();

      if (!minW && !minH && !maxW && !maxH) {
        resolve(null);
        return;
      }

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        
        const errors: string[] = [];
        
        if (minW && img.width < minW) {
          errors.push(`ancho mínimo ${minW}px`);
        }
        if (minH && img.height < minH) {
          errors.push(`alto mínimo ${minH}px`);
        }
        if (maxW && img.width > maxW) {
          errors.push(`ancho máximo ${maxW}px`);
        }
        if (maxH && img.height > maxH) {
          errors.push(`alto máximo ${maxH}px`);
        }

        if (errors.length > 0) {
          resolve({
            file,
            errorType: 'dimensions',
            message: `"${file.name}" no cumple con las dimensiones requeridas: ${errors.join(', ')}.`
          });
        } else {
          resolve(null);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({
          file,
          errorType: 'type',
          message: `"${file.name}" no es una imagen válida.`
        });
      };

      img.src = objectUrl;
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  trackByImage(index: number, image: SoImageFile): string {
    return `${image.name}-${image.size}`;
  }

  ngOnDestroy(): void {
    this.images().forEach(img => URL.revokeObjectURL(img.preview));
  }
}
